import { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Iterator from "../../utils/Iterator";
import ProductModel from "../../../Models/PorductModel";

export default function RecentlyViewed({
  recentlyViewed,
  onRecentlyClick,
  IMG_URl,
  name,
}: {
  recentlyViewed: ProductModel[];
  onRecentlyClick: (item: ProductModel) => void;
  IMG_URl: string;
  name: keyof ProductModel;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isRTL, setIsRTL] = useState(false);

  const lang = localStorage.getItem("Lang");
  useEffect(() => {
    setIsRTL(lang === "ar");
  }, [lang]);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;

    if (isRTL) {
      setCanScrollLeft(el.scrollLeft < 0);
      setCanScrollRight(
        Math.abs(el.scrollLeft) + el.clientWidth < el.scrollWidth
      );
    } else {
      setCanScrollLeft(el.scrollLeft > 0);
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth);
    }
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", checkScroll);
    return () => el.removeEventListener("scroll", checkScroll);
  }, [recentlyViewed, isRTL]);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollAmount = 200;
    const multiplier = isRTL ? -1 : 1;

    el.scrollBy({
      left:
        direction === "left"
          ? -scrollAmount * multiplier
          : scrollAmount * multiplier,
      behavior: "smooth",
    });
  };

  return (
    <div className="relative">
      {canScrollLeft && (
        <button
          className={`absolute top-1/2 -translate-y-1/2 z-10 p-2 bg-background/70 rounded-full shadow 
            ${isRTL ? "right-0" : "left-0"}`}
          onClick={() => scroll("left")}
        >
          {isRTL ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </button>
      )}

      {canScrollRight && (
        <button
          className={`absolute top-1/2 -translate-y-1/2 z-10 p-2 bg-background/70 rounded-full shadow 
            ${isRTL ? "left-0" : "right-0"}`}
          onClick={() => scroll("right")}
        >
          {isRTL ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
        </button>
      )}

      <div
        ref={scrollRef}
        className="flex overflow-x-auto gap-4 scroll-smooth scrollbar-hide"
      >
        <Iterator
          data={recentlyViewed}
          render={(item: ProductModel, i) => (
            <div
              key={item.id || i}
              className="flex flex-col gap-2 items-center text-center pb-2 rounded-xl md:rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border-2 border-primary/20 group-hover:border-primary min-w-[100px] cursor-pointer opacity-0 animate-fadeInUp  hover:scale-105"
              style={{
                animationDelay: `${i * 50}ms`,
              }}
              onClick={() => onRecentlyClick(item)}
            >
              <img
                src={`${IMG_URl}${item.image}`}
                alt={item.name_en}
                className="rounded-xl object-cover aspect-square h-24 md:h-36 w-full shadow-md hover:shadow-lg transition-shadow"
              />
              <p className="text-xs md:text-sm font-medium text-foreground line-clamp-2">
                {String(item[name as keyof ProductModel] || "")}
              </p>
            </div>
          )}
        />
      </div>
    </div>
  );
}
